import React, { FC, useContext, useMemo, useState } from "react";
import { Collapse, Switch, TableCell, TableRow, TextField, Typography } from "@mui/material";
import IconButton from "../../../components/Buttons/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DiscountedOptionGroup } from "./DiscountedProductsTable.tsx";
import { ProductOptionGroups } from "./ProductOptionGroups.tsx";
import { AutocompleteProduct } from "@jamezz/react-components";
import { DiscountedProductsTableActionMenu } from "./DiscountedProductsTableActionMenu.tsx";
import TranslationWrapper from "../TranslationWrapper/TranslationWrapper.tsx";
import { ProductAttributes } from "../../../../Domains/Products/api/productJsonApi.ts";
import _ from "lodash";
import { RenderOption } from "../../views/OptionGroups/Tabs/AutocompleteProductRenderOption.tsx";
import { ProductOptionGroupDataContext } from "./ProductAndOptionGroupContext.tsx";
import { GET_PRODUCTS } from "../../../../Domains/Products/api/productGraphQLApi.ts";

type OptionGroupProductsProps = {
  salesAreaId: number;
  optionGroup: DiscountedOptionGroup;
  onChange: (optionGroup: DiscountedOptionGroup) => void;
  onRemove: (optionGroup: DiscountedOptionGroup) => void;
};
export const OptionGroupProducts: FC<OptionGroupProductsProps> = ({ salesAreaId, optionGroup, onChange, onRemove }) => {
  const { setProductData, productData, optionGroupData } = useContext(ProductOptionGroupDataContext);
  const [isOpen, setIsOpen] = useState(false);

  const productIds: number[] = useMemo(() => {
    return (optionGroup.options ?? []).map((product) => Number(product.id));
  }, [optionGroup.options]);

  return (
    <>
      <TableRow sx={{ ".MuiTableCell-root": { borderBottom: 0, pb: 1 } }} onClick={() => setIsOpen(!isOpen)}>
        <TableCell>{optionGroup.id}</TableCell>
        <TableCell>{optionGroupData[optionGroup.id]?.name ?? optionGroup.id}</TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <TextField
            type={"number"}
            value={optionGroup.maxCount}
            onChange={(e) => onChange({ ...optionGroup, maxCount: parseInt(e.target.value) })}
          />
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={optionGroup.allOptionsCount}
            onChange={() => onChange({ ...optionGroup, allOptionsCount: !optionGroup.allOptionsCount })}
          />
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <DiscountedProductsTableActionMenu onRemove={() => onRemove(optionGroup)} />
        </TableCell>
        <TableCell align={"right"}>
          <IconButton aria-label={"expand row"} size={"small"} onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ pr: 0, py: 0 }} colSpan={6}>
          <Collapse in={isOpen} unmountOnExit>
            <Typography variant={"body2"} sx={{ mb: 1, mt: 0 }}>
              <TranslationWrapper tKey={"discounted.products"} tDefault={"Options"} />
            </Typography>
            <AutocompleteProduct<true, ProductAttributes, true>
              multiple={true}
              menuSalesareaId={salesAreaId}
              fetchQuery={GET_PRODUCTS}
              inputFieldProps={{ sx: { width: 1, mb: 1 } }}
              defaultFilters={{
                parentOptionGroupIds: {
                  value: { column: "OPTION_GROUP_ID", operator: "IN", value: [optionGroup.id] },
                },
              }}
              onFetched={(fetchedData) => {
                setProductData(_.merge({}, productData, _.keyBy(fetchedData, "id")));
              }}
              onChange={(option) => {
                onChange({
                  ...optionGroup,
                  options: _.map(option, (product) => ({
                    id: parseInt(product.id),
                    name: product.naam,
                    maxCount: 0,
                    optionGroups: [],
                  })),
                });
              }}
              value={productIds}
              renderOption={(props, option) => {
                return <RenderOption option={option} />;
              }}
            />
            {optionGroup.options?.map((product, index) => {
              return (
                <ProductOptionGroups
                  key={product.id}
                  salesAreaId={salesAreaId}
                  discountedProduct={product}
                  onChange={(changedProduct) => {
                    const options = [...(optionGroup.options ?? [])];
                    options.splice(index, 1, changedProduct);
                    onChange({ ...optionGroup, options });
                  }}
                />
              );
            })}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
